import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import { useTrips } from '../hooks/useTrips';
import { TripCard } from '../components/TripCard';
import { DeleteTripDialog } from '../components/DeleteTripDialog';
import type { Trip } from '../types';

export function TripListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trips, isLoading, error, deleteTrip } = useTrips();
  const [tripToDelete, setTripToDelete] = useState<Trip | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = (trip: Trip) => {
    navigate(`/trips/${trip.id}/edit`);
  };

  const handleDeleteClick = (trip: Trip) => {
    setTripToDelete(trip);
  };

  const handleDeleteConfirm = async () => {
    if (!tripToDelete) return;
    try {
      setIsDeleting(true);
      await deleteTrip(tripToDelete.id);
      setTripToDelete(null);
    } catch {
      // Error is handled by the hook
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('trips.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/trips/new')}
        >
          {t('trips.newTrip')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {trips.length === 0 && !error ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('trips.noTrips')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('trips.noTripsDesc')}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/trips/new')}>
            {t('trips.createFirst')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {trips.map((trip) => (
            <Grid item xs={12} sm={6} md={4} key={trip.id}>
              <TripCard trip={trip} onEdit={handleEdit} onDelete={handleDeleteClick} />
            </Grid>
          ))}
        </Grid>
      )}

      <DeleteTripDialog
        trip={tripToDelete}
        open={Boolean(tripToDelete)}
        onClose={() => setTripToDelete(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </Box>
  );
}
